import { useCallback, useMemo } from 'react';
import { StanButton, StanDropdownMenu, StanInput, StanToggle } from '@/components';
import { StanDraggable } from '@/components/core-components/StanDraggable';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { BareSvg } from '@/lib/bareSvg';
import { StanDivider } from '@/components/core-components/StanDivider/StanDivider';
import { FormFieldCard } from './FormFieldCard';
import { Question } from '@/types/store';
import { usePageDataStore } from '@/features/product-editor/pageStore';
import { DragEvent } from '@/components/core-components/StanDraggable/StanDraggable.types';

import IcUser from '@/assets/icons/ic-user-01.svg';
import IcEmail from '@/assets/icons/ic-email-flows.svg';
import IcPlus from '@/assets/icons/ic-plus.svg';
import IcPhone from '@/assets/icons/ic-phone-01.svg';
import IcTrash from '@/assets/icons/ic-x-close.svg';

const DEFAULT_FORM_FIELDS: Record<string, Omit<Question, 'type'> & { type: string }> = {
    text: { required: false, content: '', type: 'text' },
    phone: { required: false, content: 'Phone Number', type: 'phone' },
    radio: {
        required: false,
        content: '',
        type: 'radio',
        options: [{ value: '' }, { value: '' }],
    },
    dropdown: {
        required: false,
        content: '',
        type: 'dropdown',
        options: [{ value: '' }, { value: '' }],
    },
    checkbox: {
        required: false,
        content: '',
        type: 'checkbox',
        options: [{ value: '' }, { value: '' }],
    },
};

export default function FormFields() {
    const questions = usePageDataStore(state => state.pageData?.product.questions);
    const updatePageData = usePageDataStore(state => state.updatePageData);

    // Find phone field
    const phoneFieldIndex = useMemo(() => {
        return questions?.findIndex(field => field.type === 'phone') ?? -1;
    }, [questions]);

    const phoneField = useMemo(() => {
        return phoneFieldIndex >= 0 ? questions?.[phoneFieldIndex] : undefined;
    }, [questions, phoneFieldIndex]);

    const updateField = (index: number, updatedField: Question) => {
        updatePageData(state => {
            return {
                ...state,
                product: {
                    ...state.product,
                    questions: questions?.map((field, idx) => (idx === index ? updatedField : field)) ?? [],
                },
            };
        });
    };

    const removeField = (index: number) => {
        // This is because lodash merge cannot handle removals it merges arrays
        updatePageData(state => {
            return {
                ...state,
                product: {
                    ...state.product,
                    questions: questions?.filter((_, i) => i !== index) ?? [],
                },
            };
        });
    };

    const addPhoneField = useCallback(() => {
        const newField = { ...DEFAULT_FORM_FIELDS.phone, enabled: true } as Question;
        updatePageData({ product: { questions: [newField, ...(questions || [])] } });
    }, [questions, updatePageData]);

    const addField = useCallback(
        (type: string) => {
            const newField = JSON.parse(JSON.stringify(DEFAULT_FORM_FIELDS[type])) as Question;
            updatePageData({ product: { questions: [...(questions || []), newField] } });
        },
        [questions, updatePageData]
    );

    const updatePhoneRequired = useCallback(
        (required: boolean) => {
            if (phoneField) {
                const updatedPhoneField = { ...phoneField, required };
                const updatedValue = questions?.map((field, idx) =>
                    idx === phoneFieldIndex ? updatedPhoneField : field
                );
                updatePageData({ product: { questions: updatedValue } });
            }
        },
        [phoneField, phoneFieldIndex, questions, updatePageData]
    );

    const handleReorder = useCallback(
        (event: DragEvent) => {
            const newOrder = event.allItems.map(item => item.id);
            const reorderedFields = newOrder
                .map(id => questions?.find((_, idx) => `field-${idx}` === id))
                .filter(Boolean) as Question[];
            updatePageData({ product: { questions: reorderedFields } });
        },
        [questions, updatePageData]
    );

    return (
        <div className='mb-1 w-full' key={questions?.length}>
            <span className='text-label-md-bold mb-2 block'>Fields</span>
            <h3 className='text-label-sm-regular mt-2 text-[var(--text-color-neutral-fg-disabled)]'>
                Basic info fields can&apos;t be edited
            </h3>

            {/* Name Field */}
            <div className='mt-3 mb-3 flex w-full flex-col p-0'>
                <div className='flex items-center px-4 py-2'>
                    <StanInput
                        startIcon={IcUser}
                        name='name-field'
                        value='Name'
                        onChange={() => {}}
                        disabled
                        className='w-full'
                    />
                </div>
            </div>

            {/* Email Field */}
            <div className='mb-3 flex w-full flex-col p-0'>
                <div className='flex items-center px-4 py-2'>
                    <StanInput
                        startIcon={IcEmail}
                        name='email-field'
                        value='Email'
                        onChange={() => {}}
                        disabled
                        className='w-full'
                    />
                </div>
            </div>

            {/* Phone Field */}
            {phoneField && (
                <div className='mb-3 flex flex-col rounded-[7px] bg-[var(--bg-color-neutral-bg-weak)] p-0 px-1 shadow-none'>
                    <div className='flex w-full items-center'>
                        <div className='w-full flex-grow'>
                            <div className='flex items-center px-3 py-2'>
                                <StanInput
                                    startIcon={IcPhone}
                                    name='phone-field'
                                    value={phoneField.content}
                                    onChange={() => {}}
                                    disabled
                                    className='mr-3 w-full'
                                />
                            </div>
                        </div>
                        <div className='mr-3 flex items-center justify-end p-1 pr-0 pb-2'>
                            <div className='mr-3 flex items-center justify-end'>
                                <span className='pr-1 text-xs leading-none tracking-wide'>Required</span>
                                <StanToggle
                                    id='checkbox-phone'
                                    checked={phoneField.required}
                                    onChange={e => updatePhoneRequired(e.target.checked)}
                                    toggleSize='sm'
                                />
                            </div>
                            <div className='flex items-center justify-end'>
                                <button
                                    className='border-0 bg-transparent p-0'
                                    type='button'
                                    onClick={() => removeField(phoneFieldIndex)}
                                >
                                    <BareSvg
                                        SvgComponent={IcTrash}
                                        className='mb-[1px] h-[18px] w-[18px] cursor-pointer hover:opacity-70'
                                        removeFill
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Divider */}
            <div className='mx-[5px] mb-6'>
                <StanDivider />
                <h4 className='text-label-sm-regular mt-2 text-[var(--text-color-neutral-fg-disabled)]'>
                    Collect additional customer info
                </h4>
            </div>

            {/* TODO fix drag from handle only */}
            {/* TODO fix drag so that it doesnt prevent the input fields and buttons from working */}
            <StanDraggable onReorder={handleReorder} direction='vertical' gap='sm'>
                {questions?.map((field, idx) => {
                    const fieldId = `field-${idx}`;
                    if (field.type === 'phone') {
                        return null;
                    }
                    return (
                        // <DraggableWrapper key={fieldId} id={fieldId}>
                        <FormFieldCard
                            key={fieldId}
                            field={field}
                            index={idx}
                            updateField={updateField}
                            removeField={removeField}
                        />
                        // </DraggableWrapper>
                    );
                })}
            </StanDraggable>

            {/* Add Field Dropdown */}
            <div>
                <DropdownProvider>
                    <StanDropdownMenu
                        id='add-field-menu'
                        name='add-fields-form-field'
                        options={[
                            ...(!phoneField
                                ? [
                                      {
                                          label: 'Phone',
                                          value: 'phone',
                                      },
                                  ]
                                : []),
                            {
                                label: 'Text',
                                value: 'text',
                            },
                            {
                                label: 'Multiple choice',
                                value: 'radio',
                            },
                            {
                                label: 'Dropdown',
                                value: 'dropdown',
                            },
                            {
                                label: 'Checkboxes',
                                value: 'checkbox',
                            },
                        ]}
                        onSelect={option => {
                            if (option.value === 'phone') {
                                addPhoneField();
                            } else {
                                addField(option.value);
                            }
                        }}
                        className='w-[160px]'
                    >
                        <StanButton name='add-fields-form-field' variant='outline' className='w-full' icon={IcPlus}>
                            Add Field
                        </StanButton>
                    </StanDropdownMenu>
                </DropdownProvider>
            </div>
        </div>
    );
}
